import { Component, RendererComponent } from '../components';
import { NavigationItem } from '../models/NavigationItem';
import { RendererEvent, PageEvent } from '../events';

/**
 * A plugin that exposes the navigation structure of the documentation
 * to the rendered templates.
 *
 * The navigation structure is generated using the current themes
 * [[BaseTheme.getNavigation]] function. This plugins takes care that the navigation
 * is updated and passed to the render context.
 */
@Component({name: 'navigation'})
export class NavigationPlugin extends RendererComponent {
    /**
     * The navigation structure generated by the current theme.
     */
    navigation!: NavigationItem;

    /**
     * Create a new NavigationPlugin instance.
     */
    initialize() {
        this.listenTo(this.owner, {
            [RendererEvent.BEGIN]: this.onBeginRenderer,
            [PageEvent.BEGIN]:     this.onBeginPage
        });
    }

    /**
     * Triggered before the renderer starts rendering a project.
     *
     * @param event  An event object describing the current render operation.
     */
    private onBeginRenderer(event: RendererEvent) {
        this.navigation = this.owner.theme!.getNavigation(event.project);
    }

    /**
     * Triggered before a document will be rendered.
     *
     * @param page  An event object describing the current render operation.
     */
    private onBeginPage(page: PageEvent) {
        const currentItems: NavigationItem[] = [];
        (function updateItem(item: NavigationItem) {
            item.isCurrent = false;
            item.isInPath  = false;
            item.isVisible = item.isGlobals;

            if (item.url === page.url || (item.dedicatedUrls && item.dedicatedUrls.indexOf(page.url) !== -1)) {
                currentItems.push(item);
            }

            if (item.children) {
                item.children.forEach((child) => updateItem(child));
            }
        })(this.navigation);

        currentItems.forEach((item: NavigationItem | undefined) => {
            item!.isCurrent = true;

            let depth = item!.isGlobals ? -1 : 0;
            let count = 1;
            while (item) {
                item.isInPath  = true;
                item.isVisible = true;

                count += 1;
                depth += 1;
                if (item.children) {
                    count += item.children.length;
                    if (depth < 2 || count < 30) {
                        item.children.forEach((child) => {
                            child.isVisible = true;
                        });
                    }
                }

                item = item.parent;
            }
        });

        page.navigation = this.navigation;
    }
}
